import type {
    ComposableOptionsBase,
    OrderLine
} from '~/types';
import type {
    PaginatedPayload,
    CustomPagination,
    PaginatedRequest
} from 'x1-base/types';

import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { omit as lo_omit } from 'es-toolkit';

import type { NitroFetchOptions } from 'nitropack';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';

export interface UseOrderLinesOptions extends ComposableOptionsBase {}

export function useOrderLines(options: Partial<UseOrderLinesOptions> = {}) {
    const apiBasePath = '/v1/order-lines';
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    async function getItems(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {}
    ): Promise<PaginatedPayload<OrderLine> | Error> {
        const url = args?.url ?? `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {}
        };

        lo_mergeCompat(config.query, args?.params, query);

        try {
            const response = await $_fetch<CustomPagination<OrderLine>>(url, config);

            return {
                pagination: lo_omit(response, ['results']),
                data: response.results
            };
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getItem(uid: string, expand: string[] = []): Promise<OrderLine | Error> {
        const url = `${apiBasePath}/${uid}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: { expand: expand.join(',') }
        };

        try {
            return await $_fetch<OrderLine>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function exportDownload(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {},
        filename?: string
    ): Promise<void | Error> {
        if (!import.meta.client) {
            return;
        }

        const url = args?.url ?? `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            responseType: 'blob',
            headers: {
                'Accept': 'text/csv'
            },
            query: {}
        };

        // Drop pagination params; the CSV export returns all matching rows.
        lo_mergeCompat(
            config.query,
            lo_omit((args?.params || {}) as Record<string, unknown>, ['page', 'page_size']),
            query,
            {
                format: 'csv',
                all: 'true'
            }
        );

        if (!filename) {
            filename = `order-lines-${new Date().toLocaleString().replace(/[\/:,\s]/g, '-')}.csv`;
        }

        try {
            const response = await $_fetch.raw(url, config);
            const blob = response._data as Blob;
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.setAttribute('download', filename);
            a.href = downloadUrl;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    return {
        getItems,
        getItem,
        exportDownload
    };
}
